<template>
  <div class="OrderForm" v-loading="isLoading">
    <!-- Common Header component -->
    <div class="Header">
      <el-button class="stop-btn" @click="_onClickCancel()">{{
        isEditMode ? "編集を中止" : "作成を中止"
      }}</el-button>

      <el-steps
        :active="activeStep"
        finish-status="finish"
        class="Steps"
        id="step"
        align-center
      >
        <el-step
          title="生活者情報"
          description
          @click.native="_onClickStep(0)"
        ></el-step>
        <el-step
          title="商品選択"
          description
          @click.native="_onClickStep(1)"
        ></el-step>
        <el-step
          title="レイアウト"
          description
          @click.native="_onClickStep(2)"
        ></el-step>
        <el-step
          title="彫り 書き 書式"
          description
          @click.native="_onClickStep(3)"
        ></el-step>
        <el-step
          title="詳細情報入力"
          description
          @click.native="_onClickStep(4)"
        ></el-step>
        <el-step
          title="異体字選択"
          description
          @click.native="_onClickStep(5)"
        ></el-step>
        <el-step
          title="入力内容確認"
          description
          @click.native="_onClickStep(6)"
        ></el-step>
      </el-steps>
      <el-button class="draft-btn" @click="openOrderManagementDialog()"
        ><i class="el-icon-menu" /> 注文管理</el-button
      >
    </div>
    <div
      class="u-line u-line--dashed"
      style="margin: 5px 0;"
      v-if="customerName || productName"
    ></div>
    <div class="Information" style="display: flex;">
      <div class="InformationCustomerName" v-if="customerName">
        <div style="font-weight: bold;">お客様名</div>
        : {{ customerName }}
      </div>
      <div class="InformationProductName" v-if="productName">
        <div style="font-weight: bold;">商品</div>
        : {{ productName }}
      </div>
    </div>
    <div
      class="u-line u-line--dashed"
      style="margin: 5px 0; margin-bottom: 20px; "
      v-if="customerName || productName"
    ></div>
    <!--
      Dialogs
    -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="isShownModal"
      width="45%"
      append-to-body
    >
      <template v-if="contents === 'askEdit'">
        <ul>
          <li>
            変更された内容を保存しますか？
          </li>
        </ul>
        <span slot="footer" class="DialogFooter">
          <el-button @click="hideModal()">キャンセル</el-button>
          <el-button type="primary" @click="_onClickConfirmEdit()"
            >保存する</el-button
          >
        </span>
      </template>

      <!--
        Save as draft dialog
      -->
      <template v-if="contents === 'askSaveAsDraft'">
        <ul>
          <li>
            入力された内容は下書きとして保存されます
          </li>
          <li>
            注文一覧の"下書き"タブから再度参照できます。
          </li>
        </ul>
        <span slot="footer" class="DialogFooter">
          <el-button @click="hideModal()">キャンセル</el-button>
          <el-button type="primary" @click="_onClickConfirmSaveAsDraft()"
            >保存する</el-button
          >
        </span>
      </template>

      <!--
        Form Input cancel dialog
      -->
      <template v-if="contents === 'askCancelForm'">
        <ul>
          <li v-for="message in modalMessages" :key="message">{{ message }}</li>
        </ul>
        <span slot="footer" class="DialogFooter">
          <el-button @click="hideModal()">キャンセル</el-button>
          <el-button type="danger" @click="_onClickCancelForm()"
            >中止する</el-button
          >
        </span>
      </template>

      <!--
        Ask next action
      -->
      <template v-if="contents === 'askNextAction'">
        <h2 class="DialogHeading"></h2>
        <span slot="footer" class="DialogFooter">
          <el-button @click="_onClickContinue()">このまま続ける</el-button>
          <el-button type="primary" @click="_onClickFinish()"
            >終了する</el-button
          >
        </span>
      </template>
    </el-dialog>
    <el-dialog
      title="注文管理"
      :visible.sync="isShownOrderManagementModal"
      width="75%"
      append-to-body
    >
      <div
        v-if="managementErrorMessage"
        class="order-management-error"
      >
        {{ managementErrorMessage }}
      </div>
      <div class="order-management-actions">
        <el-button
          type="primary"
          :disabled="isManagementSubmitting"
          :loading="isManagementSubmitting"
          @click="openSaveConfirmFromManagement()"
        >
          注文を保存する
        </el-button>
        <template v-if="isManagementShowSendAndSignature">
          <SignatureInput
            :signature="managementParentOrderSignature"
            :isEditable="isManagementSignatureEditable"
            :label="managementParentOrderSignature ? 'サイン確認' : 'サインを入力する'"
            @onSubmit="onSubmitManagementSignature"
          />
          <el-button
            type="primary"
            :disabled="!isParentStepReady || isManagementSubmitting"
            :loading="isManagementSubmitting"
            @click="openManagementSendOrderDialog()"
          >
            注文を送信する
          </el-button>
        </template>
      </div>
      <div class="order-management-list u-pb20">
        <div
          v-for="managedOrder in managedOrdersForPreview"
          :key="managedOrder.id"
          class="order-management-item u-mb10"
        >
          <div
            class="order-management-preview-frame u-mb10"
            :class="{
              'order-management-preview-frame--editing':
                isEditingManagedOrderInManagementModal(managedOrder),
            }"
          >
            <TabletPreview
              :order="managedOrder"
              :frontColor="managedOrder.fontColors[1]"
              :backColor="managedOrder.fontColors[0]"
              :is-show-description="false"
            />
          </div>
          <div class="order-management-item-actions">
            <el-button
              type="primary"
              class="u-mt10"
              @click="switchEditingOrder(managedOrder.id)"
            >編集</el-button>
            <el-button
              v-if="managedOrder.id !== managedParentOrderId"
              type="danger"
              @click="openRemoveManagedChildConfirm(managedOrder.id)"
            >削除</el-button>
          </div>
        </div>
        <div class="order-management-item order-management-item--add u-mb10">
          <el-button
            class="order-management-add-button"
            :disabled="!isParentStepReady || isManagementSubmitting"
            :loading="isManagementSubmitting"
            @click="addChildOrder()"
          >
            <i class="el-icon-plus" />
            戒名を追加する
          </el-button>
        </div>
      </div>
    </el-dialog>
    <el-dialog
      title="戒名の削除"
      :visible.sync="isRemoveManagedChildDialogVisible"
      width="45%"
      append-to-body
      @close="onRemoveManagedChildDialogClose"
    >
      <ul>
        <li>この戒名を削除しますか？</li>
      </ul>
      <span slot="footer" class="DialogFooter">
        <el-button @click="cancelRemoveManagedChildConfirm">キャンセル</el-button>
        <el-button type="danger" @click="confirmRemoveManagedChild"
          >削除する</el-button
        >
      </span>
    </el-dialog>
    <OrderFormSendOrderDialog
      :visible.sync="managementSendOrderDialogVisible"
      :append-to-body="true"
      :show-expected-shipping-picker="isManagementExpectedShippingDateFormVisible"
      :submit-disabled="!isManagementSendOrderButtonActive || isManagementSubmitting"
      :submit-loading="isManagementSubmitting"
      @closed="closedManagementSendOrderDialog"
      @cancel="cancelManagementSendOrderDialog"
      @submit="submitManagementSendOrder()"
    />

    <!-- Actual Order Form Components are here -->
    <OrderForm ref="orderForm" />
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters, mapActions } from "vuex";
import OrderForm from "@/components/OrderForm.vue";
import TabletPreview from "@/components/Common/TabletPreview.vue";
import SignatureInput from "@/components/SignatureInput.vue";
import OrderFormSendOrderDialog from "@/components/OrderFormSendOrderDialog.vue";
import Order from "@/entities/order.entity";
import Auth from "@/services/auth.service";
import UsecaseFactory from "@/usecases/usecase.factory.js";
const orderUsecase = UsecaseFactory.create("order");
const ORDER_STATUS_CODES = [
  "DRAFT",
  "ORDER_REQUEST",
  "PROCESSING",
  "PROCESSING_CREATING_LAYOUT",
  "CANCEL_REQUEST",
  "REVISING_LAYOUT",
  "CONFIRMED",
  "SHIPPED",
];

function isPageReload () {
  if (
    typeof performance === "undefined" ||
    typeof performance.getEntriesByType !== "function"
  ) {
    return false;
  }
  const nav = performance.getEntriesByType("navigation")[0];
  return !!(nav && nav.type === "reload");
}

export default {
  components: {
    OrderForm,
    TabletPreview,
    SignatureInput,
    OrderFormSendOrderDialog,
  },
  provide () {
    return {
      getStep7OrderSubmitPayload: (status) => {
        if (
          !this.managedParentOrderId ||
          !Array.isArray(this.managedOrders) ||
          this.managedOrders.length <= 1
        ) {
          return null;
        }
        const routeId = this.$route.params.id;
        if (
          routeId != null &&
          routeId !== "" &&
          String(routeId) !== String(this.managedParentOrderId)
        ) {
          return null;
        }
        this.syncCurrentOrderToManagedOrders();
        return this.buildManagedOrderPayload(status);
      },
    };
  },
  data () {
    return {
      dialogTitle: "",
      isShownModal: false,
      isShownOrderManagementModal: false,
      contents: "",
      modalMessages: [],
      isLoading: false,
      currentStep: this.$store.state.orderForm.currentStep,
      managedOrders: [],
      managedParentOrderId: null,
      activeManagedOrderId: null,
      managementErrorMessage: "",
      isManagementSubmitting: false,
      tempChildOrderSeq: 1,
      managementSendOrderDialogVisible: false,
      managementSendOrderPrevActiveId: null,
      managementSendOrderSuccessClose: false,
      isRemoveManagedChildDialogVisible: false,
      pendingRemoveManagedChildOrderId: null,
    };
  },
  computed: {
    activeStep () {
      return this.$store.state.orderForm.currentStep;
    },
    customerName () {
      try {
        return this.order.customer.name;
      } catch {
        return "";
      }
    },
    productName () {
      try {
        return this.order.detail.product.name;
      } catch {
        return "";
      }
    },
    managedOrdersForPreview () {
      return this.managedOrders.map(managedOrder => Order.fromJson(managedOrder));
    },
    managementParentOrderSignature () {
      const parentOrder = this.managedOrders.find(
        order => order.id === this.managedParentOrderId
      );
      return parentOrder && parentOrder.detail ? parentOrder.detail.signature : null;
    },
    isManagementSignatureEditable () {
      return this.isParentStepReady || !this.managementParentOrderSignature;
    },
    isManagementExpectedShippingDateFormVisible () {
      const { isEditMode, order } = this;
      const { status } = order;
      if (
        !isEditMode ||
        status === "DRAFT" ||
        status === "ORDER_REQUEST"
      ) {
        return true;
      }
      return false;
    },
    hasManagementExpectedShippingDate () {
      const form = this.$store.state.orderForm.detailInput;
      return !!(form && form.expectedShippingDate);
    },
    isManagementSendOrderButtonActive () {
      if (!this.isEditMode) {
        return true;
      }
      if (!this.isManagementExpectedShippingDateFormVisible) {
        return true;
      }
      return this.hasManagementExpectedShippingDate;
    },
    isParentStepReady () {
      const parentOrder = this.managedOrders.find(
        order => order.id === this.managedParentOrderId
      );
      if (!parentOrder || !parentOrder.detail) {
        return false;
      }
      const detail = parentOrder.detail;
      const typography = detail.typography || {};
      const hasTypography = !!(
        typography.printType &&
        typography.font &&
        typography.textColorFront &&
        typography.textColorBack
      );
      return !!(
        parentOrder.clientGroup &&
        parentOrder.clientGroup.id &&
        detail.product &&
        detail.product.id &&
        detail.productVariation &&
        detail.productVariation.id &&
        hasTypography
      );
    },
    isManagementClientContriburor () {
      const user = this.$store.state.user;
      return !!(user && Auth.isClientContriburor(user.role));
    },
    /**
     * OrderFormStep7 の computedShowRequestOrderButton に合わせ、
     * CLIENT_USER 向けのサイン・注文送信を出す条件（子注文編集時は非表示）。
     */
    isManagementShowSendAndSignature () {
      if (this.isManagementClientContriburor) {
        return false;
      }
      const parent = this.managedOrders.find(
        order => order.id === this.managedParentOrderId
      );
      if (!parent) {
        return false;
      }
      const parentOrderId =
        parent.parentOrderId ||
        (parent.parentOrder && parent.parentOrder.id) ||
        null;
      const status = this.normalizeOrderStatus(parent.status, null);

      if (!this.isEditMode && !parentOrderId) {
        return true;
      }
      if (this.isEditMode && status === "DRAFT" && !parentOrderId) {
        return true;
      }
      return false;
    },
    ...mapGetters({
      isEditMode: "orderForm/isEditMode",
      order: "orderForm/order",
      _baseUrl: "orderForm/_baseUrl",
    }),
  },
  watch: {
    managedOrders: {
      deep: true,
      handler () {
        this.schedulePersistManagedFamily();
      },
    },
    managedParentOrderId () {
      this.schedulePersistManagedFamily();
    },
    activeManagedOrderId () {
      this.schedulePersistManagedFamily();
    },
  },
  created () {
    this.showLoader();
  },
  async mounted () {
    // console.log(this.currentStep);
    this.$store.commit("orderForm/setMode", "create");
    const { id } = this.$route.params;
    const { parentOrderId } = this.order;
    const parentOrderIdForApi =
      parentOrderId &&
      !this.isTemporaryManagedOrderId(String(parentOrderId))
        ? parentOrderId
        : null;

    // fetch custom field config
    const fetchOrderCustomFieldConfig = async (_order) => {
      if (!_order || !_order.clientGroup || !_order.clientGroup.id) {
        return { data: null };
      }
      const clientGroupId = _order.clientGroup.id;
      const options = { clientGroupId };
      const res = await orderUsecase.getOrderCustomFieldConfig(options);

      return res;
    };

    if (id) {
      const snap = this.$store.state.orderForm.managedFamilyPersist;
      const reload = isPageReload();

      if (!reload) {
        this.$store.commit("orderForm/setManagedFamilyPersist", null);
      }

      const restoreSnap =
        reload && this.isManagedFamilySnapRestorableForEdit(snap, id);

      if (restoreSnap) {
        this.managedOrders = snap.managedOrders.map(order =>
          this.cloneOrder(order)
        );
        this.managedParentOrderId = snap.managedParentOrderId;
        if (typeof snap.tempChildOrderSeq === "number") {
          this.tempChildOrderSeq = snap.tempChildOrderSeq;
        }
        const routeOrderId = String(id);
        let activeId =
          snap.activeManagedOrderId != null
            ? String(snap.activeManagedOrderId)
            : routeOrderId;
        if (!snap.managedOrders.some(o => String(o.id) === activeId)) {
          activeId = routeOrderId;
        }
        if (!snap.managedOrders.some(o => String(o.id) === activeId)) {
          activeId = String(snap.managedParentOrderId);
        }
        this.activeManagedOrderId = activeId;

        const parentRow = this.managedOrders.find(
          o => String(o.id) === String(this.managedParentOrderId)
        );
        let orderCustomFieldConfig = { data: null };
        if (parentRow && parentRow.clientGroup && parentRow.clientGroup.id) {
          orderCustomFieldConfig = await fetchOrderCustomFieldConfig(
            parentRow
          );
        }
        this.$store.commit("orderForm/setMode", "edit");
        this.setCustomFields(
          orderCustomFieldConfig,
          (parentRow && parentRow.customFields) || []
        );
        this.$nextTick(() => {
          this.loadOrderIntoForm(activeId);
        });
      } else {
        if (reload && snap) {
          this.$store.commit("orderForm/setManagedFamilyPersist", null);
        }

        const currentOrderId = id;
        const currentOrder = await orderUsecase.getOrder({
          orderId: currentOrderId,
        });
        const isChildOrder = !!(
          currentOrder &&
          currentOrder.parentOrder &&
          currentOrder.parentOrder.id
        );
        const parentOrderIdForLoad = isChildOrder
          ? currentOrder.parentOrder.id
          : currentOrder.id;
        const parentOrder = await orderUsecase.getOrder({
          orderId: parentOrderIdForLoad,
        });

        const orderCustomFieldConfig = await fetchOrderCustomFieldConfig(
          parentOrder
        );
        const childOrders = Array.isArray(parentOrder.childOrders)
          ? parentOrder.childOrders
          : [];
        this.managedOrders = [parentOrder, ...childOrders].map(order =>
          this.cloneOrder(order)
        );
        this.managedParentOrderId = parentOrder.id;
        this.activeManagedOrderId = currentOrderId;

        this.$store.commit("orderForm/setMode", "edit");
        this.loadOrderIntoForm(currentOrderId);
        this.setCustomFields(orderCustomFieldConfig, parentOrder.customFields);
      }
    } else if (parentOrderIdForApi) {
      // DB に存在する親注文 ID のときのみ API で親を取得（tmp-parent-* は未保存のため除外）
      const parentOrder = await orderUsecase.getOrder({
        orderId: parentOrderIdForApi,
      });

      // get custom field config
      const orderCustomFieldConfig = await fetchOrderCustomFieldConfig(parentOrder);

      // set child order params
      const childOrder = {};

      childOrder.parentOrderId = parentOrderId;
      childOrder.status = parentOrder.status;
      childOrder.customer = parentOrder.customer;
      childOrder.clientGroup = parentOrder.clientGroup;
      childOrder.deliveryDestination = parentOrder.deliveryDestination;
      childOrder.customFields = parentOrder.customFields;
      childOrder.detail = parentOrder.detail;

      this.$store.commit("orderForm/setChildOrderForm", childOrder);
      this.setCustomFields(orderCustomFieldConfig, parentOrder.customFields);
      this.$refs.orderForm.onSetOrderForm();

      const newChildId = this.generateTempChildOrderId();
      const managedParent = this.cloneOrder(parentOrder);
      const managedChild = this.buildChildFromParent(managedParent, newChildId);
      this.managedOrders = [managedParent, managedChild];
      this.managedParentOrderId = managedParent.id;
      this.activeManagedOrderId = newChildId;
    } else {
      const snap = this.$store.state.orderForm.managedFamilyPersist;
      const orderIdInStore = this.$store.state.orderForm.orderId;
      const canRestoreSnap =
        snap &&
        Array.isArray(snap.managedOrders) &&
        snap.managedOrders.length >= 1 &&
        snap.managedParentOrderId &&
        snap.managedOrders.some(
          o => String(o.id) === String(snap.managedParentOrderId)
        ) &&
        (
          String(snap.managedParentOrderId).startsWith("tmp-parent-") ||
          (orderIdInStore &&
            snap.managedOrders.some(o => String(o.id) === String(orderIdInStore)))
        );

      if (canRestoreSnap) {
        this.managedOrders = snap.managedOrders.map(order => this.cloneOrder(order));
        this.managedParentOrderId = snap.managedParentOrderId;
        this.activeManagedOrderId =
          snap.activeManagedOrderId || snap.managedParentOrderId;
        if (typeof snap.tempChildOrderSeq === "number") {
          this.tempChildOrderSeq = snap.tempChildOrderSeq;
        }
        const parentRow = this.managedOrders.find(
          o => String(o.id) === String(this.managedParentOrderId)
        );
        if (parentRow && parentRow.clientGroup && parentRow.clientGroup.id) {
          const orderCustomFieldConfig = await fetchOrderCustomFieldConfig(
            parentRow
          );
          this.setCustomFields(
            orderCustomFieldConfig,
            parentRow.customFields || []
          );
        }
        const loadId =
          orderIdInStore &&
          snap.managedOrders.some(o => String(o.id) === String(orderIdInStore))
            ? orderIdInStore
            : this.activeManagedOrderId;
        this.$nextTick(() => {
          this.loadOrderIntoForm(loadId);
        });
      } else {
        const newParentOrder = this.cloneOrder(this.order);
        const parentId = newParentOrder.id || this.generateTempParentOrderId();
        newParentOrder.id = parentId;
        this.managedOrders = [newParentOrder];
        this.managedParentOrderId = parentId;
        this.activeManagedOrderId = parentId;
      }
    }

    this.hideLoader();
    this.loadDefaultExpectedShippingDate();
  },
  beforeDestroy () {
    if (this._managedFamilyPersistTimer != null) {
      clearTimeout(this._managedFamilyPersistTimer);
      this._managedFamilyPersistTimer = null;
    }
  },

  methods: {
    ...mapActions({
      loadDefaultExpectedShippingDate: "orderForm/loadDefaultExpectedShippingDate",
    }),
    showLoader () {
      this.isLoading = true;
    },
    hideLoader () {
      this.isLoading = false;
    },
    async _onClickConfirmEdit () {
      // I confirmed edit
      // so I save the existing input as order
      const loading = this.$loading();
      try {
        // save it
        const res = await this.saveForms();
        if (res.errors) {
          throw new Error(res.errors[0].message);
        }

        // it succeed, so I notify it
        const message = "注文を編集しました";
        this.$notify({ message });

        // I need to confirm whether I finish or continue
        this.showConfirmModal();
      } catch (err) {
        console.log(err);
        const message = "編集に失敗しました。再度お試しください";
        this.hideModal();
        this.$notify({ message });
      } finally {
        loading.close();
      }
    },
    async _onClickConfirmSaveAsDraft () {
      // I confirmed save as draft
      // so I save the existing input as order
      const loading = this.$loading();
      try {
        // save it
        const createdOrder = await this.saveForms({ status: "DRAFT" });
        if (createdOrder.errors) {
          throw new Error(createdOrder.errors[0].message);
        }

        // it succeed, so I notify it
        const message = "下書きを作成しました";
        this.$notify({ message });

        // transition to edit mode
        this.$router.push(
          `/orders/${createdOrder.id}/edit/${this.$route.path.split("/").pop()}`
        );
        this.$store.commit("orderForm/setMode", "edit");
        this.$store.commit("orderForm/setStatusInput", "DRAFT");

        // I need to confirm whether I finish or continue
        this.showConfirmModal();
      } catch (err) {
        console.log(err);
        const message = "下書き作成に失敗しました。再度お試しください";
        this.hideModal();
        this.$notify({ message });
      } finally {
        loading.close();
      }
    },
    _onClickCancelForm () {
      this.finishForm();
    },
    _onClickFinish () {
      this.finishForm();
    },
    _onClickContinue () {
      // I chose continue to input form
      // so I close modal
      this.hideModal();
    },
    _onClickStep (step) {
      this.syncCurrentOrderToManagedOrders();
      const { $router } = this;
      const { parentOrderId } = this.order;
      let url = this._baseUrl;

      if (step > this.$store.state.orderForm.currentStep) {
        return;
      }

      switch (step) {
        case 0:
          if (parentOrderId) {
            // don't show step1 when creating/updating child order
            return;
          } else {
            url = url + "/step1";
          }
          break;
        case 1:
          if (parentOrderId) {
            // don't show step2 when creating/updating child order
            return;
          } else {
            url = url + "/step2";
          }
          break;
        case 2:
          url = url + "/step4";
          break;
        case 3:
          if (parentOrderId) {
            // don't show step5 when creating/updating child order
            return;
          } else {
            url = url + "/step5";
          }
          break;
        case 4:
          url = url + "/step6";
          break;
        case 5:
          url = url + "/revise-character";
          break;
        case 6:
          url = url + "/step7";
          break;
      }

      $router.push({
        path: url,
      });
    },
    _onClickCancel () {
      this.showModal();
      this.dialogTitle = "入力を中止しますか?";
      this.contents = "askCancelForm";
      if (this.isEditMode) {
        this.modalMessages = [
          "フォームの入力を中止して、注文詳細画面に戻ります",
          "編集を中止された場合、変更内容の復元はできません",
          "編集した内容を保存する場合は、保存ボタンをご利用ください",
        ];
        return;
      }
      this.modalMessages = [
        "フォームの入力を中止して、ホーム画面に戻ります",
        "中止された場合入力内容は復元できません",
      ];
    },
    _onClickSave () {
      this.showModal();
      if (this.isEditMode || this.order.parentOrderId) {
        // this is edit mode:
        this.dialogTitle = "保存しますか?";
        this.contents = "askEdit";
        return;
      }
      // save as draft
      this.dialogTitle = "下書き保存しますか?";
      this.contents = "askSaveAsDraft";
    },
    openSaveConfirmFromManagement () {
      this.syncCurrentOrderToManagedOrders();
      this._onClickSave();
    },
    openOrderManagementDialog () {
      this.syncCurrentOrderToManagedOrders();
      this.managementErrorMessage = "";
      this.isShownOrderManagementModal = true;
    },
    isEditingManagedOrderInManagementModal (managedOrder) {
      return (
        this.activeManagedOrderId != null &&
        String(this.activeManagedOrderId) === String(managedOrder.id)
      );
    },
    openManagementSendOrderDialog () {
      this.syncCurrentOrderToManagedOrders();
      if (this.activeManagedOrderId !== this.managedParentOrderId) {
        this.managementSendOrderPrevActiveId = this.activeManagedOrderId;
        this.loadOrderIntoForm(this.managedParentOrderId);
      } else {
        this.managementSendOrderPrevActiveId = null;
      }
      this.managementSendOrderDialogVisible = true;
    },
    cancelManagementSendOrderDialog () {
      this.managementSendOrderDialogVisible = false;
    },
    closedManagementSendOrderDialog () {
      if (this.managementSendOrderSuccessClose) {
        this.managementSendOrderSuccessClose = false;
        this.$store.dispatch("orderForm/resetDetailInput");
        this.managementSendOrderPrevActiveId = null;
        return;
      }
      this.$store.dispatch("orderForm/resetDetailInput");
      const prevActive = this.managementSendOrderPrevActiveId;
      this.managementSendOrderPrevActiveId = null;
      const restoreId =
        prevActive != null ? prevActive : this.managedParentOrderId;
      this.loadOrderIntoForm(restoreId);
    },
    async submitManagementSendOrder () {
      this.syncCurrentOrderToManagedOrders();
      const res = await this.saveManagedOrders("PROCESSING");
      if (!res) {
        return;
      }
      this.managementSendOrderSuccessClose = true;
      this.managementSendOrderDialogVisible = false;
    },
    cloneOrder (order) {
      return JSON.parse(JSON.stringify(order || {}));
    },
    /**
     * 編集ルートの URL id（親または子）が、保持中の managedFamilyPersist と同一ファミリーか。
     */
    isManagedFamilySnapRestorableForEdit (snap, routeOrderId) {
      if (
        !snap ||
        !Array.isArray(snap.managedOrders) ||
        snap.managedOrders.length < 1 ||
        !snap.managedParentOrderId
      ) {
        return false;
      }
      if (
        !snap.managedOrders.some(
          o => String(o.id) === String(snap.managedParentOrderId)
        )
      ) {
        return false;
      }
      const rid = String(routeOrderId);
      if (rid === String(snap.managedParentOrderId)) {
        return true;
      }
      return snap.managedOrders.some(o => String(o.id) === rid);
    },
    persistManagedFamily () {
      if (
        !this.managedParentOrderId ||
        !Array.isArray(this.managedOrders) ||
        this.managedOrders.length === 0
      ) {
        this.$store.commit("orderForm/setManagedFamilyPersist", null);
        return;
      }
      this.$store.commit("orderForm/setManagedFamilyPersist", {
        managedOrders: this.managedOrders.map(order => this.cloneOrder(order)),
        managedParentOrderId: this.managedParentOrderId,
        activeManagedOrderId: this.activeManagedOrderId,
        tempChildOrderSeq: this.tempChildOrderSeq,
      });
    },
    schedulePersistManagedFamily () {
      if (this._managedFamilyPersistTimer != null) {
        clearTimeout(this._managedFamilyPersistTimer);
      }
      this._managedFamilyPersistTimer = setTimeout(() => {
        this._managedFamilyPersistTimer = null;
        this.persistManagedFamily();
      }, 150);
    },
    generateTempParentOrderId () {
      return `tmp-parent-${Date.now()}`;
    },
    generateTempChildOrderId () {
      const childOrderId = `tmp-child-${Date.now()}-${this.tempChildOrderSeq}`;
      this.tempChildOrderSeq += 1;
      return childOrderId;
    },
    findManagedOrderIndex (orderId) {
      return this.managedOrders.findIndex(order => order.id === orderId);
    },
    isChildManagedOrderById (orderId) {
      return !!orderId && orderId !== this.managedParentOrderId;
    },
    isTemporaryManagedOrderId (orderId) {
      return (
        typeof orderId === "string" &&
        (orderId.startsWith("tmp-parent-") || orderId.startsWith("tmp-child-"))
      );
    },
    resolveCurrentManagedOrderId () {
      const orderIdInStore = this.$store.state.orderForm.orderId;
      const hasStoreOrder = this.findManagedOrderIndex(orderIdInStore) !== -1;
      if (hasStoreOrder) {
        return orderIdInStore;
      }
      return this.activeManagedOrderId;
    },
    syncCurrentOrderToManagedOrders () {
      const syncTargetOrderId = this.resolveCurrentManagedOrderId();
      if (!syncTargetOrderId) {
        return;
      }
      const currentOrderSnapshot = this.cloneOrder(this.order);
      if (!currentOrderSnapshot.id) {
        currentOrderSnapshot.id = syncTargetOrderId;
      }
      const currentIndex = this.findManagedOrderIndex(syncTargetOrderId);
      const existingOrder = currentIndex === -1 ? null : this.managedOrders[currentIndex];
      if (this.isChildManagedOrderById(syncTargetOrderId)) {
        if (!currentOrderSnapshot.parentOrder && existingOrder && existingOrder.parentOrder) {
          currentOrderSnapshot.parentOrder = this.cloneOrder(existingOrder.parentOrder);
        }
        if (!currentOrderSnapshot.parentOrderId && existingOrder && existingOrder.parentOrderId) {
          currentOrderSnapshot.parentOrderId = existingOrder.parentOrderId;
        }
        if (!currentOrderSnapshot.parentOrder && this.managedParentOrderId) {
          currentOrderSnapshot.parentOrder = { id: this.managedParentOrderId };
        }
        if (!currentOrderSnapshot.parentOrderId && this.managedParentOrderId) {
          currentOrderSnapshot.parentOrderId = this.managedParentOrderId;
        }
      }
      // _.merge は配列を index 単位で再帰マージするため、異体字配列の文字数減少
      // （例: 九十一 → 九十）が反映されない。配列はソース側で置換する。
      const mergedSnapshot = _.mergeWith(
        {},
        this.cloneOrder(existingOrder || {}),
        currentOrderSnapshot,
        (objValue, srcValue) => {
          if (Array.isArray(srcValue)) {
            return srcValue;
          }
        }
      );
      mergedSnapshot.id = syncTargetOrderId;
      if (currentIndex === -1) {
        this.managedOrders.push(mergedSnapshot);
      } else {
        this.managedOrders.splice(currentIndex, 1, mergedSnapshot);
      }
      this.activeManagedOrderId = syncTargetOrderId;
    },
    normalizeManagedOrderForForm (order) {
      const normalized = this.cloneOrder(order);
      if (
        normalized.id !== this.managedParentOrderId &&
        !normalized.parentOrder &&
        this.managedParentOrderId
      ) {
        normalized.parentOrder = { id: this.managedParentOrderId };
      }
      const parentRow = this.managedOrders.find(
        o => o.id === this.managedParentOrderId
      );
      if (
        parentRow &&
        normalized.id !== this.managedParentOrderId
      ) {
        if (!normalized.clientGroup && parentRow.clientGroup) {
          normalized.clientGroup = this.cloneOrder(parentRow.clientGroup);
        }
        if (!normalized.deliveryDestination && parentRow.deliveryDestination) {
          normalized.deliveryDestination = this.cloneOrder(
            parentRow.deliveryDestination
          );
        }
      }
      return normalized;
    },
    loadOrderIntoForm (orderId) {
      const targetOrder = this.managedOrders.find(order => order.id === orderId);
      if (!targetOrder) {
        return;
      }
      const normalizedTargetOrder = this.normalizeManagedOrderForForm(targetOrder);
      this.$store.commit("orderForm/setOrderId", orderId);
      this.$store.commit("orderForm/setOrderForm", normalizedTargetOrder);
      this.$refs.orderForm.onSetOrderForm();
      this.activeManagedOrderId = orderId;
    },
    /**
     * 子注文は step4（位牌種類・レイアウト）と step6（詳細入力）を親から引き継がない。
     * product / quantity / typography（step5）などは親と揃えたままにする。
     */
    resetManagedOrderDetailStep6 (detail) {
      if (!detail) {
        return;
      }
      detail.orderType = null;
      detail.layout = null;
      detail.columns = [];
      detail.underCharacters = [];
      detail.underCharactersPlacement = null;
      detail.secularNamePrefixPlacement = null;
      detail.ageAtDeathPrefixPlacement = null;
      detail.secularNamePrefix = [];
      detail.isDiedCharacterNeeded = null;
      detail.diedCharacter = [];
      detail.ageAtDeathPrefix = null;
      detail.ageUnitCharacter = [];
      detail.hostName = [];
      detail.sanskritCharacterOrFamilyEmblem = null;
      detail.sanskritCharacter = null;
      detail.familyEmblem = null;
      detail.sanskritCharacterBack = null;
      detail.isSanskritCharacterBackNeeded = null;
      detail.customSanskritCharacterUrl = null;
      detail.customSanskritCharacterBackUrl = null;
      detail.customFamilyEmblemUrl = null;
      detail.signature = null;
    },
    buildChildFromParent (parentOrder, childOrderId) {
      const childOrder = this.cloneOrder(parentOrder);
      childOrder.id = childOrderId;
      childOrder.parentOrder = { id: parentOrder.id };
      childOrder.parentOrderId = parentOrder.id;
      childOrder.childOrders = [];
      if (childOrder.detail) {
        this.resetManagedOrderDetailStep6(childOrder.detail);
      }
      return childOrder;
    },
    addChildOrder () {
      this.syncCurrentOrderToManagedOrders();
      const parentOrder = this.managedOrders.find(
        order => order.id === this.managedParentOrderId
      );
      if (!parentOrder) {
        return;
      }
      const childOrderId = this.generateTempChildOrderId();
      const childOrder = this.buildChildFromParent(parentOrder, childOrderId);
      this.managedOrders.push(childOrder);
      this.switchEditingOrder(childOrderId);
    },
    openRemoveManagedChildConfirm (orderId) {
      if (orderId == null || String(orderId) === String(this.managedParentOrderId)) {
        return;
      }
      this.pendingRemoveManagedChildOrderId = orderId;
      this.isRemoveManagedChildDialogVisible = true;
    },
    cancelRemoveManagedChildConfirm () {
      this.isRemoveManagedChildDialogVisible = false;
      this.pendingRemoveManagedChildOrderId = null;
    },
    onRemoveManagedChildDialogClose () {
      this.pendingRemoveManagedChildOrderId = null;
    },
    confirmRemoveManagedChild () {
      const orderId = this.pendingRemoveManagedChildOrderId;
      this.isRemoveManagedChildDialogVisible = false;
      this.pendingRemoveManagedChildOrderId = null;
      if (orderId != null) {
        this.removeChildOrder(orderId);
      }
    },
    removeChildOrder (orderId) {
      if (orderId === this.managedParentOrderId) {
        return;
      }
      const orderIndex = this.findManagedOrderIndex(orderId);
      if (orderIndex === -1) {
        return;
      }
      const storeOrderId = this.$store.state.orderForm.orderId;
      const wasEditingRemoved =
        String(this.activeManagedOrderId) === String(orderId) ||
        String(storeOrderId) === String(orderId);
      this.managedOrders.splice(orderIndex, 1);
      if (wasEditingRemoved) {
        // 削除直後は Vuex 上の orderId がまだ子のまま。先に sync すると findIndex=-1 で子行が push され復活する。
        this.switchEditingOrder(this.managedParentOrderId, { skipInitialSync: true });
      }
    },
    switchEditingOrder (orderId, options = {}) {
      const { skipInitialSync = false } = options;
      if (!skipInitialSync) {
        this.syncCurrentOrderToManagedOrders();
      }
      this.loadOrderIntoForm(orderId);
      const targetOrder = this.managedOrders.find(order => order.id === orderId);
      if (!targetOrder) {
        return;
      }
      const nextPath = this.isChildManagedOrderById(orderId)
        ? `${this._baseUrl}/step4`
        : `${this._baseUrl}/step1`;
      this.$router.push({ path: nextPath });
      this.isShownOrderManagementModal = false;
    },
    onSubmitManagementSignature (signature) {
      const parentIndex = this.findManagedOrderIndex(this.managedParentOrderId);
      if (parentIndex === -1) {
        return;
      }
      const parentOrder = this.cloneOrder(this.managedOrders[parentIndex]);
      parentOrder.detail = parentOrder.detail || {};
      parentOrder.detail.signature = signature;
      this.managedOrders.splice(parentIndex, 1, parentOrder);

      if (this.activeManagedOrderId === this.managedParentOrderId) {
        this.$store.commit("orderForm/setOrderForm", this.normalizeManagedOrderForForm(parentOrder));
        this.$refs.orderForm.onSetOrderForm();
      }
    },
    normalizeOrderStatus (rawStatus, fallback = null) {
      if (
        typeof rawStatus === "string" &&
        ORDER_STATUS_CODES.includes(rawStatus)
      ) {
        return rawStatus;
      }
      if (
        rawStatus &&
        typeof rawStatus === "object" &&
        ORDER_STATUS_CODES.includes(rawStatus.code)
      ) {
        return rawStatus.code;
      }
      return fallback;
    },
    buildManagedOrderPayload (status) {
      const requestedStatus = this.normalizeOrderStatus(status, null);
      const defaultStatus = requestedStatus || (!this.isEditMode ? "DRAFT" : null);
      const parentOrderData = this.managedOrders.find(
        order => order.id === this.managedParentOrderId
      );
      const parentOrderEntity = Order.fromJson(parentOrderData || {});
      const payload = Object.assign(parentOrderEntity.orderCreatePayload);
      const parentStatus = this.normalizeOrderStatus(
        payload.order.status,
        defaultStatus
      );
      if (parentStatus) {
        payload.order.status = parentStatus;
      } else {
        delete payload.order.status;
      }
      const parentExpectedShippingDate =
        payload.order.detail &&
        payload.order.detail.expectedShippingDate != null
          ? payload.order.detail.expectedShippingDate
          : "";
      const childOrdersPayload = this.managedOrders
        .filter(order => order.id !== this.managedParentOrderId)
        .map(childOrder => {
          const childForPayload = {
            ...childOrder,
            clientGroup:
              childOrder.clientGroup ||
              (parentOrderData && parentOrderData.clientGroup),
            deliveryDestination:
              childOrder.deliveryDestination ||
              (parentOrderData && parentOrderData.deliveryDestination),
          };
          const childEntity = Order.fromJson(childForPayload);
          const childPayload = childEntity.orderCreatePayload.order;
          const childStatus = this.normalizeOrderStatus(
            childPayload.status,
            parentStatus || defaultStatus
          );
          if (childStatus) {
            childPayload.status = childStatus;
          } else {
            delete childPayload.status;
          }
          // OrderUpdateInput(childOrders) には parentOrderId が存在しないため常に除外する
          delete childPayload.parentOrderId;
          if (this.isEditMode) {
            if (
              childOrder.id &&
              !this.isTemporaryManagedOrderId(childOrder.id)
            ) {
              childPayload.id = childOrder.id;
            } else {
              delete childPayload.id;
            }
          } else {
            delete childPayload.id;
          }
          if (!childPayload.detail) {
            childPayload.detail = {};
          }
          childPayload.detail.expectedShippingDate = parentExpectedShippingDate;
          return childPayload;
        });

      payload.order.childOrders = childOrdersPayload;
      if (requestedStatus) {
        payload.order.status = requestedStatus;
      }
      return payload;
    },
    extractErrorMessage (err, fallback) {
      if (err && err.errors && err.errors[0] && err.errors[0].message) {
        return err.errors[0].message;
      }
      if (err && err.message) {
        return err.message;
      }
      return fallback;
    },
    updateManagementOrderHomeState (status) {
      const item = sessionStorage.getItem("homeState");

      if (item) {
        const values = JSON.parse(item);
        values.status = status;

        sessionStorage.setItem("homeState", JSON.stringify(values));
      }
    },
    fetchManagementProcessingCompleteMessage () {
      const parentOrderData = this.managedOrders.find(
        order => order.id === this.managedParentOrderId
      );
      const order = Order.fromJson(parentOrderData || {});
      const {
        quantity,
        productVariation,
        orderType,
      } = order.detail;
      const { stock } = productVariation;
      const { nameCount } = orderType;

      if (stock < quantity * nameCount) {
        return "ご注文ありがとうございました。誠に申し訳ございません。只今在庫欠品中の為、納期の確認を致します。";
      }

      return "ご注文ありがとうございました。お手続きを進めさせていただきます。";
    },
    /**
     * 編集モード orderUpdate 成功後、サーバー上の親子構成で managedOrders を置き換える。
     * 新規作成された子の tmp ID を実 ID に揃え、編集中なら Vuex・ルートも追従する。
     */
    async refreshManagedFamilyAfterEditOrderUpdate ({
      leavingEditToParentDetail = false,
    } = {}) {
      const parentId = this.managedParentOrderId;
      if (!parentId || this.isTemporaryManagedOrderId(String(parentId))) {
        return;
      }
      const prevActiveId = this.activeManagedOrderId;
      const routeOrderId = this.$route.params.id;

      let childSlotIndex = null;
      if (prevActiveId != null && String(prevActiveId) !== String(parentId)) {
        const idx = this.findManagedOrderIndex(prevActiveId);
        if (idx >= 1) {
          childSlotIndex = idx - 1;
        }
      }

      const parentOrder = await orderUsecase.getOrder({ orderId: parentId });
      const childOrders = Array.isArray(parentOrder.childOrders)
        ? parentOrder.childOrders
        : [];

      this.managedParentOrderId = parentOrder.id;
      this.managedOrders = [
        this.cloneOrder(parentOrder),
        ...childOrders.map(order => this.cloneOrder(order)),
      ];

      if (leavingEditToParentDetail) {
        return;
      }

      let newActiveId = this.managedParentOrderId;
      if (
        childSlotIndex != null &&
        childOrders[childSlotIndex] &&
        childOrders[childSlotIndex].id != null
      ) {
        newActiveId = childOrders[childSlotIndex].id;
      }

      this.activeManagedOrderId = newActiveId;
      this.loadOrderIntoForm(newActiveId);

      if (routeOrderId == null) {
        return;
      }
      const stepMatch = this.$route.path.match(/^\/orders\/[^/]+\/edit\/(.+)$/);
      if (
        stepMatch &&
        String(routeOrderId) !== String(newActiveId)
      ) {
        this.$router.replace(
          `/orders/${newActiveId}/edit/${stepMatch[1]}`
        );
      }
    },
    async saveManagedOrders (status = null) {
      this.managementErrorMessage = "";
      this.syncCurrentOrderToManagedOrders();
      this.isManagementSubmitting = true;
      try {
        let response = null;
        const payload = this.buildManagedOrderPayload(status);
        if (this.isEditMode && this.managedParentOrderId) {
          payload.orderId = this.managedParentOrderId;
          delete payload.order.parentOrderId;
          const res = await orderUsecase.orderUpdate(payload);
          if (res && res.errors) {
            throw res;
          }
          await this.refreshManagedFamilyAfterEditOrderUpdate({
            leavingEditToParentDetail: status === "PROCESSING",
          });
          response = {
            ...(res || {}),
            id: this.managedParentOrderId,
          };
          if (status === "PROCESSING") {
            this.updateManagementOrderHomeState(status);
            this.$notify({
              message: this.fetchManagementProcessingCompleteMessage(),
              duration: 3000,
            });
            this.$router.push(`/orders/${this.managedParentOrderId}`);
          }
        } else {
          const res = await orderUsecase.orderCreate(payload);
          if (res && res.errors) {
            throw res;
          }
          const createdParentOrderId = res.id;
          if (status === "PROCESSING") {
            this.updateManagementOrderHomeState(status);
            this.$notify({
              message: this.fetchManagementProcessingCompleteMessage(),
              duration: 3000,
            });
            this.$router.push(`/orders/${createdParentOrderId}`);
          } else {
            this.$store.commit("orderForm/setMode", "edit");
            this.$store.commit("orderForm/setOrderId", createdParentOrderId);
            this.managedParentOrderId = createdParentOrderId;
            this.activeManagedOrderId = createdParentOrderId;
            this.$router.push(`/orders/${createdParentOrderId}/edit/step1`);
            const parentOrder = await orderUsecase.getOrder({ orderId: createdParentOrderId });
            this.managedOrders = [
              this.cloneOrder(parentOrder),
              ...(Array.isArray(parentOrder.childOrders)
                ? parentOrder.childOrders.map(order => this.cloneOrder(order))
                : []),
            ];
            this.loadOrderIntoForm(createdParentOrderId);
          }
          response = res;
        }
        this.isShownOrderManagementModal = false;
        return response;
      } catch (err) {
        const fallback = status === "PROCESSING"
          ? "注文送信に失敗しました。再度お試しください"
          : "注文保存に失敗しました。再度お試しください";
        this.managementErrorMessage = this.extractErrorMessage(err, fallback);
      } finally {
        this.isManagementSubmitting = false;
      }
    },
    showConfirmModal () {
      this.showModal();
      this.dialogTitle = "このまま作業を続けますか？";
      this.contents = "askNextAction";
    },
    finishForm () {
      if (this.isEditMode) {
        this.goToDetail();
      } else {
        this.goToHome();
      }

      // 初期化
      this.orderFormInit();
    },
    async saveForms (order = {}) {
      return this.saveManagedOrders(order.status || null);
    },
    showModal () {
      this.isShownModal = true;
    },
    hideModal () {
      this.isShownModal = false;
    },
    goToHome () {
      const { $router } = this;
      $router.push("/home");
    },
    goToDetail () {
      const { id } = this.$route.params;
      const targetId = this.managedParentOrderId || id;
      this.$router.push(`/orders/${targetId}`);
    },
    orderFormInit () {
      this.$store.commit("orderForm/init");
    },
    // --------
    // DEPRECATED
    // --------
    async saveDraftOrder (type) {
      this.continue = false;
      this.draftDialog = false;
      this.items = this.$store.state.orderForm.customerInput;
      if (Object.keys(this.items).length !== 0) {
        await orderUsecase.orderCreate({
          order: {
            status: "DRAFT",
            detail: {},
            customer: {
              name: this.items.name,
              sect: this.items.sect,
              zipcode: this.items.zipcode,
              address: this.items.address,
              tel: this.items.tel,
              note: this.items.note,
              familyTemple: this.items.familyTemple,
              fortyNinthMemorialServiceDate: this.fortyNinthMemorialServiceDate,
            },
          },
        });
      }
      if (type === "complete") {
        this.$router.push("/home");
      }
      this.$notify({
        title: "下書き保存しました",
        duration: 3000,
      });
      this.$store.commit("orderForm/init");
    },

    /**
     * [setCustomFields description]
     * @param   {[type]}  res           [res description]
     * @param   {[type]}  customFields  [customFields description]
     */
    setCustomFields (res, orderCustomFields) {
      if (!res.data) {
        return;
      }

      const itemSettings = [];

      // 管理画面で登録された自由項目から enabled の項目のみ取得する
      res.data.orderCustomFieldConfig.itemSettings
        .sort((a, b) => {
          return a.order < b.order ? -1 : 1;
        })
        .forEach(itemSetting => {
          if (itemSetting.enabled) {
            itemSettings.push(itemSetting);
          }
        });

      // 得意先での自由項目の入力形式で形成
      // ※ 型が変更されていたときは初期化する
      const fetchValueByType = (type, name, target, defaultValue) => {
        if (
          target &&
          target.type === type
        ) {
          return target.value;
        }

        _.defer(() => {
          this.$notify.warning({
            title: "自由項目が変更されています",
            message: `対象の項目: 「${name}」`,
            duration: 8 * 1000,
          });
        });

        return defaultValue || null;
      };

      const customFields = itemSettings.map((itemSetting) => {
        const {
          customFieldNumber,
          type,
          name,
          order,
          defaultValue,
        } = itemSetting;
        const target = orderCustomFields.find(orderCustomField => orderCustomField.customFieldNumber === customFieldNumber);
        const value = fetchValueByType(type, name, target, defaultValue);
        const customField = {
          customFieldNumber,
          type,
          name,
          order,
          value,
        };

        return customField;
      });

      this.$store.commit("orderForm/setItemSettings", itemSettings);
      this.$store.commit("orderForm/setCustomFields", customFields);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

.DialogFooter {
  text-emphasis: right;
}
.Header {
  display: flex;
  margin-bottom: 5px;
}
.HeaderOrderCancel {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.Steps {
  flex: 1;
  margin-top: 5px;
}
.btn-text {
  padding-top: 4rem;
  width: 50%;
  color: $text-lighter;
}
.DialogHeading {
  margin: 0;
}
.stop-btn {
  color: $red-base; // $red-light
  border: 2px solid $red-base; // $red-light
  margin-top: 10px;
  margin-left: 10px;
}
.draft-btn {
  color: $blue-base; // #7acdfc;
  border: 2px solid $blue-base; // #7acdfc;
  margin-top: 10px;
  margin-right: 10px;
}
</style>

<style lang="scss">
@import "~@/assets/styles/variables.scss";

#OrderRequest .el-dialog__headerbtn .el-dialog__close {
  color: white;
}
.OrderForm {
  height: 100%;
}
.Information {
  margin-left: 10px;
}
.InformationCustomerName {
  display: flex;
  width: 250px;
  margin-right: 15px;
}
.InformationProductName {
  display: flex;
  width: 200px;
  margin-right: 10px;
}
.order-management-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.order-management-error {
  color: $red-base;
  margin-bottom: 10px;
  text-align: center;
}
.order-management-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 20px;
}
.order-management-item {
  padding: 12px;
}
.order-management-item--add {
  display: flex;
  height: 472px
}
.order-management-add-button {
  width: 100%;
  min-height: 260px;
  border: 1px dashed $blue-base;
  color: $blue-base;
  font-size: 16px;
}
.order-management-preview-frame {
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 12px 10px;
  background: #fff;
}
/* OrderFormStep3 .isActiveProduct と同じ（商品選択の選択中カード） */
.order-management-preview-frame--editing {
  border: 3px solid $blue-deep;
}
.order-management-item-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}
</style>
